"use client";

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, AlertCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";

export type ToastType = "success" | "error" | "info";

export interface ToastProps {
  id: string;
  title: string;
  description?: string;
  type?: ToastType;
  duration?: number;
  onClose: (id: string) => void;
}

export const Toast = ({
  id,
  title,
  description,
  type = "info",
  duration = 5000,
  onClose,
}: ToastProps) => {
  const [progress, setProgress] = useState(0);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Start progress timer
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      const newProgress = Math.min((elapsedTime / duration) * 100, 100);
      setProgress(newProgress);

      if (elapsedTime >= duration) {
        onClose(id);
      }
    }, 10);

    setIntervalId(interval);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [duration, id, onClose]);

  const handleClose = () => {
    if (intervalId) clearInterval(intervalId);
    onClose(id);
  };

  const handleMouseEnter = () => {
    if (intervalId) clearInterval(intervalId);
  };

  const handleMouseLeave = () => {
    // Resume progress when mouse leaves
    const remainingTime = duration * (1 - progress / 100);
    const startTime = Date.now();
    
    const interval = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      const additionalProgress = Math.min((elapsedTime / remainingTime) * (100 - progress), 100 - progress);
      const newProgress = progress + additionalProgress;
      
      setProgress(newProgress);

      if (newProgress >= 100) {
        onClose(id);
      }
    }, 10);

    setIntervalId(interval);
  };

  // Icon based on type
  const TypeIcon = {
    success: <CheckCircle className="h-5 w-5 text-emerald-500" />,
    error: <AlertCircle className="h-5 w-5 text-red-500" />,
    info: <Info className="h-5 w-5 text-blue-500" />,
  }[type];

  // Background and border based on type
  const toastStyles = {
    success: "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800",
    error: "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800",
    info: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
  }[type];

  // Text color based on type
  const textStyles = {
    success: "text-emerald-800 dark:text-emerald-200",
    error: "text-red-800 dark:text-red-200",
    info: "text-blue-800 dark:text-blue-200",
  }[type];

  // Progress bar color based on type
  const progressStyles = {
    success: "bg-emerald-500",
    error: "bg-red-500",
    info: "bg-blue-500",
  }[type];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className={cn(
        "relative w-full max-w-sm overflow-hidden rounded-lg border shadow-lg",
        "p-4 backdrop-blur-sm",
        toastStyles
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">{TypeIcon}</div>
        <div className="ml-3 w-0 flex-1">
          <p className={cn("text-sm font-medium", textStyles)}>{title}</p>
          {description && (
            <p className={cn("mt-1 text-sm opacity-90", textStyles)}>
              {description}
            </p>
          )}
        </div>
        <div className="ml-4 flex flex-shrink-0">
          <button
            className={cn(
              "inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2",
              "text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
            )}
            onClick={handleClose}
          >
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700">
        <div
          className={cn("h-full", progressStyles)}
          style={{ width: `${progress}%` }}
        />
      </div>
    </motion.div>
  );
};
